import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { ACHIEVEMENTS, generateJobs, WEEK_LENGTH, BUSINESSES } from "@/data/gameData";
import { defaultBrand } from "@/data/brandData";
import { Sound } from "@/lib/sound";
import { DEFAULT_AVATAR } from "@/data/avatarData";
import { verifyUnlock, registerLeaderboardPlayer } from "@/lib/payments";

const STORAGE_KEY = "bizkids_save_v1";

const emptyEarnings = () => ({
  dog_walker: { revenue: 0, cost: 0, profit: 0, jobs: 0 },
  babysitter: { revenue: 0, cost: 0, profit: 0, jobs: 0 },
  craft_maker: { revenue: 0, cost: 0, profit: 0, jobs: 0 },
  lawn_mower: { revenue: 0, cost: 0, profit: 0, jobs: 0 },
});

const addEarnings = (bucket, businessId, price, cost) => {
  const cur = bucket[businessId] || { revenue: 0, cost: 0, profit: 0, jobs: 0 };
  return {
    ...bucket,
    [businessId]: {
      revenue: cur.revenue + price,
      cost: cur.cost + cost,
      profit: cur.profit + (price - cost),
      jobs: cur.jobs + 1,
    },
  };
};

// Riley's customers leave reviews too: nudge his rating and keep it in a fair range
const driftRivalRep = (rep, delta) => Number(Math.min(4.8, Math.max(3.0, rep + delta)).toFixed(1));

const defaultState = {
  playerName: "",
  avatar: DEFAULT_AVATAR,
  earnings: emptyEarnings(),
  todayEarnings: emptyEarnings(),
  flyerLeads: {}, // leads earned today -> customers tomorrow
  activeLeads: {}, // extra customers available today
  flyers: {}, // saved flyer designs per business
  brands: {}, // custom business name/logo/color per business
  totalLeads: 0,
  rival: { reputation: 4.0, weekEarnings: 0, totalEarnings: 0, stolen: 0, weekStolen: 0 },
  weekEarnings: 0,
  week: 1,
  trophies: 0,
  weekResults: [],
  lastWeekResult: null,
  unlocked: false,
  unlockEmailHint: null,
  unlockRef: null,
  unlockToken: null,
  paywallSeen: false,
  landingDone: false,
  lastAutoTipDay: 0,
  saveCode: null,
  leaderboardId: null,
  leaderboardToken: null,
  cash: 10, // starter allowance
  piggyBank: 0,
  reputation: 3.0, // out of 5
  safetyScore: 3, // out of 5
  day: 1,
  dailyJobsCompleted: 0,
  totalJobs: 0,
  totalEarned: 0,
  ownedSupplies: [], // supply ids
  triedBusinesses: [], // business ids
  achievements: [], // achievement ids
  safetyCorrect: 0,
  jobs: {
    dog_walker: [],
    babysitter: [],
    craft_maker: [],
    lawn_mower: [],
  },
  seenTips: [],
};

const GameContext = createContext(null);

export const useGame = () => {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be inside <GameProvider>");
  return ctx;
};

export const GameProvider = ({ children }) => {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          ...defaultState,
          ...parsed,
          avatar: { ...DEFAULT_AVATAR, ...(parsed.avatar || {}) },
          earnings: { ...emptyEarnings(), ...(parsed.earnings || {}) },
          todayEarnings: { ...emptyEarnings(), ...(parsed.todayEarnings || {}) },
          rival: { ...defaultState.rival, ...(parsed.rival || {}) },
          jobs: { ...defaultState.jobs, ...(parsed.jobs || {}) },
        };
      }
    } catch (e) {
      console.warn("Saved game could not be loaded, starting fresh", e);
    }
    return defaultState;
  });

  const [floatingCoins, setFloatingCoins] = useState([]); // {id, amount, x, y}
  const [confettiOn, setConfettiOn] = useState(false);
  const [avatarMakerOpen, setAvatarMakerOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const FREE_BUSINESS = "dog_walker";
  const isBusinessLocked = (businessId) => !state.unlocked && businessId !== FREE_BUSINESS;
  // cred = { unlock_ref, unlock_token } issued by the server after a verified payment / admin code
  const setUnlocked = (emailHint, cred) =>
    setState((s) => ({
      ...s, unlocked: true, unlockEmailHint: emailHint || s.unlockEmailHint, paywallSeen: true,
      unlockRef: cred?.unlock_ref || s.unlockRef, unlockToken: cred?.unlock_token || s.unlockToken,
    }));
  const markPaywallSeen = () => setState((s) => ({ ...s, paywallSeen: true }));
  const markLandingDone = () => setState((s) => ({ ...s, landingDone: true }));
  const relock = () => setState((s) => ({ ...s, unlocked: false, unlockEmailHint: null, unlockRef: null, unlockToken: null, paywallSeen: false }));
  const markAutoTipDay = () => setState((s) => ({ ...s, lastAutoTipDay: s.day }));

  // Server-side entitlement check: an "unlocked" flag without a valid server token is dropped
  useEffect(() => {
    if (!state.unlocked) return;
    if (!state.unlockRef || !state.unlockToken) { relock(); return; }
    verifyUnlock(state.unlockRef, state.unlockToken).then((ok) => { if (!ok) relock(); }).catch(() => {});
  }, [state.unlocked, state.unlockRef, state.unlockToken]); // eslint-disable-line

  // Replace the whole game with a loaded save (keeps unlock if either side has a real token)
  const loadState = (loaded, code) =>
    setState((s) => {
      const keepOwn = s.unlocked && s.unlockToken;
      const cred = keepOwn ? s : loaded;
      return {
        ...defaultState,
        ...loaded,
        avatar: { ...DEFAULT_AVATAR, ...(loaded.avatar || {}) },
        earnings: { ...emptyEarnings(), ...(loaded.earnings || {}) },
        todayEarnings: { ...emptyEarnings(), ...(loaded.todayEarnings || {}) },
        rival: { ...defaultState.rival, ...(loaded.rival || {}) },
        jobs: { ...defaultState.jobs, ...(loaded.jobs || {}) },
        unlocked: !!(cred.unlocked && cred.unlockToken),
        unlockRef: cred.unlockRef || null,
        unlockToken: cred.unlockToken || null,
        landingDone: true,
        saveCode: code,
      };
    });
  const setSaveCode = (code) => setState((s) => ({ ...s, saveCode: code }));
  // Server-issued leaderboard identity (id + signed token) so nobody can post as someone else
  const ensureLeaderboardIdentity = async () => {
    if (state.leaderboardId && state.leaderboardToken) return { player_id: state.leaderboardId, token: state.leaderboardToken };
    const { player_id, token } = await registerLeaderboardPlayer();
    setState((s) => ({ ...s, leaderboardId: player_id, leaderboardToken: token }));
    return { player_id, token };
  };
  // Boss Score: total money the kid has (cash in hand + piggy bank)
  const bossScore = () => Math.max(0, Math.round(state.cash + state.piggyBank));

  // persist
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.warn("Progress could not be saved", e);
    }
  }, [state]);

  const setPlayerName = (name) => setState((s) => ({ ...s, playerName: name }));
  const setAvatar = (avatar) => setState((s) => ({ ...s, avatar: { ...DEFAULT_AVATAR, ...avatar } }));

  const spawnCoins = (amount) => {
    const id = Date.now() + Math.random();
    setFloatingCoins((c) => [...c, { id, amount }]);
    if (amount > 0) Sound.coin();
    setTimeout(() => {
      setFloatingCoins((c) => c.filter((x) => x.id !== id));
    }, 1200);
  };

  const triggerConfetti = () => {
    setConfettiOn(true);
    Sound.fanfare();
    setTimeout(() => setConfettiOn(false), 2600);
  };

  const checkAchievements = useCallback((s) => {
    const newlyEarned = [];
    ACHIEVEMENTS.forEach((a) => {
      if (s.achievements.includes(a.id)) return;
      let earned = false;
      if (a.id === "first_dollar" && s.totalJobs >= 1) earned = true;
      if (a.id === "hundred_club" && s.totalEarned >= 100) earned = true;
      if (a.id === "five_star" && s.reputation >= 5) earned = true;
      if (a.id === "saver" && s.piggyBank >= 50) earned = true;
      if (a.id === "all_businesses" && s.triedBusinesses.length >= 4) earned = true;
      if (a.id === "safe_pro" && s.safetyCorrect >= 3) earned = true;
      if (a.id === "marketer" && s.totalLeads >= 3) earned = true;
      if (a.id === "top_boss" && s.trophies >= 1) earned = true;
      if (earned) newlyEarned.push(a.id);
    });
    return newlyEarned;
  }, []);

  const completeJob = ({ jobId, businessId, price, cost, rating }) => {
    setState((s) => {
      const profit = price - cost;
      const newRep = Math.min(5, Math.max(0, s.reputation * 0.85 + rating * 0.15));
      const newTried = s.triedBusinesses.includes(businessId)
        ? s.triedBusinesses
        : [...s.triedBusinesses, businessId];
      const newState = {
        ...s,
        cash: s.cash + profit,
        totalEarned: s.totalEarned + Math.max(0, profit),
        totalJobs: s.totalJobs + 1,
        dailyJobsCompleted: s.dailyJobsCompleted + 1,
        reputation: Number(newRep.toFixed(2)),
        rival: { ...s.rival, reputation: driftRivalRep(s.rival.reputation, Math.random() < 0.6 ? 0 : (Math.random() < 0.5 ? -0.1 : 0.1)) },
        triedBusinesses: newTried,
        earnings: addEarnings(s.earnings, businessId, price, cost),
        todayEarnings: addEarnings(s.todayEarnings, businessId, price, cost),
        weekEarnings: s.weekEarnings + profit,
        jobs: {
          ...s.jobs,
          [businessId]: s.jobs[businessId].filter((j) => j.id !== jobId),
        },
      };
      const newAch = checkAchievements(newState);
      newState.achievements = [...newState.achievements, ...newAch];
      return newState;
    });
    spawnCoins(price - cost);
  };

  const declineJob = (businessId, jobId) => {
    setState((s) => ({
      ...s,
      jobs: {
        ...s.jobs,
        [businessId]: s.jobs[businessId].filter((j) => j.id !== jobId),
      },
    }));
  };

  const loseJobToRival = (businessId, jobId, rivalPrice) => {
    Sound.error();
    setState((s) => ({
      ...s,
      jobs: { ...s.jobs, [businessId]: s.jobs[businessId].filter((j) => j.id !== jobId) },
      rival: {
        ...s.rival,
        reputation: driftRivalRep(s.rival.reputation, 0.1),
        weekEarnings: s.rival.weekEarnings + rivalPrice,
        totalEarnings: s.rival.totalEarnings + rivalPrice,
        stolen: s.rival.stolen + 1,
        weekStolen: s.rival.weekStolen + 1,
      },
    }));
  };

  const markWeekSeen = () =>
    setState((s) => ({ ...s, lastWeekResult: s.lastWeekResult ? { ...s.lastWeekResult, seen: true } : null }));

  const refreshJobs = (businessId) => {
    setState((s) => ({
      ...s,
      jobs: { ...s.jobs, [businessId]: generateJobs(businessId, s.day, s.activeLeads[businessId] || 0) },
    }));
  };

  const spendMarketing = (businessId, amount) => {
    let ok = false;
    setState((s) => {
      if (s.cash < amount) return s;
      ok = true;
      return {
        ...s,
        cash: s.cash - amount,
        earnings: addEarnings(s.earnings, businessId, 0, amount),
        todayEarnings: addEarnings(s.todayEarnings, businessId, 0, amount),
      };
    });
    if (ok) { Sound.purchase(); spawnCoins(-amount); }
    return ok;
  };

  const addLeads = (businessId, leads, penalties = 0) => {
    setState((s) => {
      const cur = s.flyerLeads[businessId] || 0;
      const added = Math.max(0, Math.min(3 - cur, leads));
      const rep = Math.max(0, s.reputation - penalties * 0.1);
      const newState = {
        ...s,
        flyerLeads: { ...s.flyerLeads, [businessId]: cur + added },
        totalLeads: s.totalLeads + added,
        reputation: Number(rep.toFixed(2)),
        safetyScore: penalties > 0 ? Math.max(0, s.safetyScore - 1) : s.safetyScore,
      };
      const newAch = checkAchievements(newState);
      newState.achievements = [...newState.achievements, ...newAch];
      return newState;
    });
  };

  const saveFlyer = (businessId, design) =>
    setState((s) => ({ ...s, flyers: { ...s.flyers, [businessId]: design } }));

  const saveBrand = (businessId, brand) =>
    setState((s) => ({ ...s, brands: { ...s.brands, [businessId]: brand } }));

  // Brand for a business: custom if set, else default "<Name>'s <Business>"
  const getBrand = (businessId) => {
    const biz = BUSINESSES[businessId];
    return { ...defaultBrand(biz, state.playerName), ...(state.brands[businessId] || {}) };
  };

  const buySupply = (supply) => {
    setState((s) => {
      if (s.cash < supply.price) return s;
      if (s.ownedSupplies.includes(supply.id)) return s;
      return {
        ...s,
        cash: s.cash - supply.price,
        ownedSupplies: [...s.ownedSupplies, supply.id],
      };
    });
  };

  const depositToPiggy = (amount) => {
    setState((s) => {
      const amt = Math.min(amount, s.cash);
      if (amt <= 0) return s;
      const newState = { ...s, cash: s.cash - amt, piggyBank: s.piggyBank + amt };
      const newAch = checkAchievements(newState);
      newState.achievements = [...newState.achievements, ...newAch];
      return newState;
    });
  };

  const withdrawFromPiggy = (amount) => {
    setState((s) => {
      const amt = Math.min(amount, s.piggyBank);
      if (amt <= 0) return s;
      return { ...s, cash: s.cash + amt, piggyBank: s.piggyBank - amt };
    });
  };

  const endDay = () => {
    setState((s) => {
      // 5% piggy bank interest each day
      const interest = Math.floor(s.piggyBank * 0.05);
      // Riley works too: earns a bit every day and reputation drifts
      const rivalDaily = 14 + Math.floor(Math.random() * 18) + (s.day > 3 ? 4 : 0);
      const swing = (Math.random() < 0.5 ? -1 : 1) * (0.1 + Math.floor(Math.random() * 3) * 0.1); // ±0.1..0.3
      let rival = {
        ...s.rival,
        prevReputation: s.rival.reputation,
        reputation: driftRivalRep(s.rival.reputation, swing),
        weekEarnings: s.rival.weekEarnings + rivalDaily,
        totalEarnings: s.rival.totalEarnings + rivalDaily,
      };
      const weekOver = s.day % WEEK_LENGTH === 0;
      let { weekEarnings, week, trophies, weekResults, lastWeekResult } = s;
      if (weekOver) {
        const won = weekEarnings > rival.weekEarnings;
        lastWeekResult = { week, you: weekEarnings, rival: rival.weekEarnings, stolen: rival.weekStolen, won, seen: false };
        weekResults = [...weekResults, lastWeekResult];
        if (won) trophies += 1;
        week += 1;
        weekEarnings = 0;
        rival = { ...rival, weekEarnings: 0, weekStolen: 0 };
      }
      const newState = {
        ...s,
        day: s.day + 1,
        dailyJobsCompleted: 0,
        todayEarnings: emptyEarnings(),
        activeLeads: { ...s.flyerLeads },
        flyerLeads: {},
        piggyBank: s.piggyBank + interest,
        rival,
        weekEarnings,
        week,
        trophies,
        weekResults,
        lastWeekResult,
        jobs: {
          dog_walker: [],
          babysitter: [],
          craft_maker: [],
          lawn_mower: [],
        },
      };
      if (weekOver) {
        const newAch = checkAchievements(newState);
        newState.achievements = [...newState.achievements, ...newAch];
      }
      return newState;
    });
    triggerConfetti();
  };

  const markTipSeen = (tipId, correct) => {
    setState((s) => {
      if (s.seenTips.includes(tipId)) return s;
      const isSafetyTip = tipId === "safety" || tipId === "marketing";
      const newState = {
        ...s,
        seenTips: [...s.seenTips, tipId],
        cash: correct ? s.cash + 5 : s.cash,
        safetyCorrect: isSafetyTip && correct ? s.safetyCorrect + 1 : s.safetyCorrect,
        safetyScore: isSafetyTip && correct ? Math.min(5, s.safetyScore + 1) : s.safetyScore,
      };
      const newAch = checkAchievements(newState);
      newState.achievements = [...newState.achievements, ...newAch];
      return newState;
    });
    if (correct) spawnCoins(5);
  };

  const keepUnlock = (s) => ({
    unlocked: s.unlocked, unlockEmailHint: s.unlockEmailHint, unlockRef: s.unlockRef, unlockToken: s.unlockToken,
    leaderboardId: s.leaderboardId, leaderboardToken: s.leaderboardToken, landingDone: s.landingDone,
  });

  const resetGame = () => {
    setState((s) => ({ ...defaultState, ...keepUnlock(s), paywallSeen: s.unlocked }));
  };

  // Fresh start from $0: keeps name, avatar, flyers, purchase; wipes money & progress
  const resetMoney = () => {
    Sound.whoosh();
    setState((s) => ({
      ...defaultState,
      cash: 0,
      playerName: s.playerName,
      avatar: s.avatar,
      flyers: s.flyers,
      seenTips: s.seenTips,
      ...keepUnlock(s),
      paywallSeen: s.paywallSeen,
    }));
  };

  const applyBoosts = (businessId, basePrice, baseCost) => {
    // apply owned supply boosts
    let price = basePrice;
    let cost = baseCost;
    let ratingBoost = 0;
    state.ownedSupplies.forEach((id) => {
      const s = require("@/data/gameData").SUPPLIES.find((x) => x.id === id);
      if (!s || s.business !== businessId) return;
      if (s.boostValue) price += s.boostValue;
      if (s.ratingBoost) ratingBoost += s.ratingBoost;
      if (s.costCut) cost = Math.max(0, cost - s.costCut);
    });
    return { price, cost, ratingBoost };
  };

  return (
    <GameContext.Provider
      value={{
        state,
        setPlayerName,
        setAvatar,
        avatarMakerOpen,
        setAvatarMakerOpen,
        paywallOpen,
        setPaywallOpen,
        isBusinessLocked,
        setUnlocked,
        relock,
        markPaywallSeen,
        markLandingDone,
        markAutoTipDay,
        loadState,
        setSaveCode,
        ensureLeaderboardIdentity,
        bossScore,
        completeJob,
        declineJob,
        refreshJobs,
        spendMarketing,
        addLeads,
        saveFlyer,
        saveBrand,
        getBrand,
        loseJobToRival,
        markWeekSeen,
        triggerConfetti,
        buySupply,
        depositToPiggy,
        withdrawFromPiggy,
        endDay,
        markTipSeen,
        resetGame,
        resetMoney,
        applyBoosts,
        floatingCoins,
        confettiOn,
        spawnCoins,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};
